import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../../services/ApiService";
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import BookingDetailsOffcanvas from "../../../components/BookingDetailsOffcanvas";
import DateRangePicker from "../../../components/DateRangePicker";
import { exportBookings } from "../../../utils/exportHelper";
import "./List.css";

const TABS = [
  { value: "",          label: "All Bookings" },
  { value: "Pending",   label: "Pending"      },
  { value: "Confirmed", label: "Confirmed"    },
  { value: "Completed", label: "Completed"    },
  { value: "Cancelled", label: "Cancelled"    },
  // { value: "Refunded",  label: "Refunded"     },
];

const STATUS_OPTIONS = [
  { value: "",          label: "All Status" },
  { value: "Pending",   label: "Pending"    },
  { value: "Confirmed", label: "Confirmed"  },
  { value: "Late",      label: "Late"       },
  { value: "No Show",   label: "No Show"    },
  { value: "Completed", label: "Completed"  },
  { value: "Cancelled", label: "Cancelled"  },
  { value: "Refunded",  label: "Refunded"   },
];

const SOURCE_OPTIONS = [
  { value: "",      label: "Walking/App" },
  { value: "app",   label: "App"       },
  { value: "admin", label: "Walking"     },
];

const findStatusOption = (value) =>
  STATUS_OPTIONS.find((o) => o.value.toLowerCase() === String(value || "").toLowerCase());

const findSourceOption = (value) =>
  SOURCE_OPTIONS.find((o) => o.value.toLowerCase() === String(value || "").toLowerCase());

const statusPillClass = (status) => {
  const map = {
    pending:   "status-pill status-pending",
    confirmed: "status-pill status-confirmed",
    completed: "status-pill status-completed",
    cancelled: "status-pill status-cancelled",
    refunded:  "status-pill status-refunded",
    active:    "status-pill status-confirmed",
    late:      "status-pill status-late",
    "no show": "status-pill status-no-show",
  };
  return map[status?.toLowerCase()] || "status-pill status-default";
};

const BookingsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoader, setIsLoader] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(searchParams.get("booking") || null);
  const [showBookingDetails, setShowBookingDetails] = useState(!!searchParams.get("booking"));
  const [isExporting, setIsExporting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total_records: 0,
    total_pages: 0,
  });
  const [apiData, setApiData] = useState({
    search: "",
    daterange: "",
    status: searchParams.get("status") || "",
    payment_status: "",
    booking_source: searchParams.get("booking_source") || "",
    page: 1,
  });

  useEffect(() => {
    getBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData.page, apiData.search, apiData.daterange, apiData.status, apiData.payment_status, apiData.booking_source]);

  // Close status/source dropdowns when clicking outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!e.target.closest(".booking-status")) setShowStatusDropdown(false);
      if (!e.target.closest(".booking-source-filter")) setShowSourceDropdown(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Keep the offcanvas in sync with the `booking` query param. selectedBookingId/
  // showBookingDetails are only *seeded* from it on mount — without this, a "View
  // Booking" navigation that lands back on this same page (e.g. from
  // NewBookingPopup while a different booking's offcanvas is already open) updates
  // the URL but the still-mounted component keeps showing the old booking.
  useEffect(() => {
    const bookingId = searchParams.get("booking");
    if (bookingId) {
      setSelectedBookingId(bookingId);
      setShowBookingDetails(true);
    } else {
      setShowBookingDetails(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("booking")]);

  /* ── API calls ── */

  const getBookings = async () => {
    try {
      setIsLoader(true);
      const params = new URLSearchParams();
      if (apiData.search) params.append("search", apiData.search);
      if (apiData.daterange?.[0] && apiData.daterange?.[1]) {
        const toLocalYMD = (d) => {
          const dt = new Date(d);
          return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
        };
        params.append("date_from", toLocalYMD(apiData.daterange[0]));
        params.append("date_to",   toLocalYMD(apiData.daterange[1]));
      }
      if (apiData.status)         params.append("status",         apiData.status);
      if (apiData.payment_status) params.append("payment_status", apiData.payment_status);
      if (apiData.booking_source) params.append("booking_source", apiData.booking_source);
      if (apiData.page)           params.append("page",           apiData.page);
      params.append("limit", "20");

      const res = await ApiService.request({
        method: "GET",
        url: `admin/bookings?${params.toString()}`,
      });

      if (res.data.status) {
        setBookings(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Failed to fetch bookings");
    } finally {
      setIsLoader(false);
    }
  };

  /* ── Handlers ── */

  const setStatus = (status) => {
    setApiData((p) => ({ ...p, status, page: 1 }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (status) next.set("status", status);
      else next.delete("status");
      return next;
    }, { replace: true });
  };

  const setSource = (booking_source) => {
    setApiData((p) => ({ ...p, booking_source, page: 1 }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (booking_source) next.set("booking_source", booking_source);
      else next.delete("booking_source");
      return next;
    }, { replace: true });
  };

  const handleBookingClick = (booking) => {
    setSelectedBookingId(booking._id);
    setShowBookingDetails(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("booking", booking._id);
      return next;
    }, { replace: true });
  };

  const handleCloseOffcanvas = () => {
    setShowBookingDetails(false);
    setSelectedBookingId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("booking");
      return next;
    }, { replace: true });
  };

  const handleExportClick = async (format) => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      await exportBookings(
        format,
        { search: apiData.search, daterange: apiData.daterange, status: apiData.status, payment_status: apiData.payment_status, booking_source: apiData.booking_source, sort_by: "createdAt", sort_order: "desc" },
        () => toast.info(`Exporting to ${format.toUpperCase()}…`, { autoClose: 2000 }),
        () => toast.success(`Exported to ${format.toUpperCase()} successfully!`, { autoClose: 3000 }),
        (err) => toast.error(`Export failed: ${err.message || "Unknown error"}`, { autoClose: 5000 }),
      );
    } catch (err) {
      toast.error(`Export failed: ${err.message || "Unknown error"}`, { autoClose: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  /* ── Helpers ── */

  // A booking holds services[] of { service_id (populated), room_id, duration }.
  const serviceName = (b) => {
    const list = b.services || [];
    if (!list.length) return "—";
    const first = list[0]?.service_id?.title || "—";
    return list.length > 1 ? `${first} +${list.length - 1}` : first;
  };

  const roomName = (b) => {
    const name = b.room_id?.title || '';
    return name;
  };

  const formatDay = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  // A manual/walk-in booking has no user_id — fall back to the guest name it was booked under.
  const customerName = (b) => b.user_id?.name || b.guest_name || "N/A";

  const avatarUrl = (user, fallbackName) =>
    user?.picture || user?.image ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || fallbackName || "U")}&background=E3F0FC&color=4F97D8&size=80`;

  const activeTab = (apiData.status || "").toLowerCase();

  // "Showing X to Y of Z entries"
  const perPage = pagination.per_page || 20;
  const currentPage = pagination.current_page || 1;
  const totalRecords = pagination.total_records || 0;
  const fromEntry = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toEntry = Math.min(currentPage * perPage, totalRecords);

  /* ── Render ── */

  return (
    <>
      <div className="bookings-page">
        {/* Page Header */}
        <div className="bookings-page-header">
          <div>
            <h1 className="bookings-title">Bookings</h1>
            <div className="bookings-breadcrumb">Dashboard <i className="bi bi-chevron-right"></i> Bookings</div>
          </div>
          <div className="bookings-header-right">
            <div className="bookings-daterange">
              <DateRangePicker
                value={apiData.daterange}
                onChange={(dr) => setApiData((p) => ({ ...p, daterange: dr, page: 1 }))}
                placeholder="Select Date Range"
              />
            </div>
            <div className="dropdown">
              <button
                className="btn export-btn dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                disabled={isExporting}
              >
                <i className="bi bi-download me-2"></i>
                {isExporting ? "Exporting…" : "Export"}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                {["csv", "excel", "pdf"].map((fmt) => (
                  <li key={fmt}>
                    <button className="dropdown-item" type="button" onClick={() => handleExportClick(fmt)} disabled={isExporting}>
                      <i className={`bi bi-filetype-${fmt === "excel" ? "xlsx" : fmt} me-2`}></i>
                      {fmt.toUpperCase()}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bookings-card">
          {/* Tabs */}
          <div className="bookings-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.value || "all"}
                className={`bookings-tab ${activeTab === tab.value.toLowerCase() ? "active" : ""}`}
                onClick={() => setStatus(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="bookings-filters">
            <div className="bookings-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by booking ID, customer or service…"
                value={apiData.search}
                onChange={(e) => setApiData((p) => ({ ...p, search: e.target.value, page: 1 }))}
              />
            </div>

            <div className="booking-status">
              <button className="filter-select" onClick={() => setShowStatusDropdown((v) => !v)}>
                {findStatusOption(apiData.status)?.label || "All Status"}
                <i className="bi bi-chevron-down"></i>
              </button>
              {showStatusDropdown && (
                <div className="status-dropdown">
                  {STATUS_OPTIONS.map((opt) => (
                    <div
                      key={opt.value || "all"}
                      className={`status-dropdown-item ${activeTab === opt.value.toLowerCase() ? "active" : ""}`}
                      onClick={() => { setStatus(opt.value); setShowStatusDropdown(false); }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="booking-source-filter">
              <button className="filter-select" onClick={() => setShowSourceDropdown((v) => !v)}>
                {findSourceOption(apiData.booking_source)?.label || "Walking/App"}
                <i className="bi bi-chevron-down"></i>
              </button>
              {showSourceDropdown && (
                <div className="status-dropdown">
                  {SOURCE_OPTIONS.map((opt) => (
                    <div
                      key={opt.value || "all"}
                      className={`status-dropdown-item ${apiData.booking_source === opt.value ? "active" : ""}`}
                      onClick={() => { setSource(opt.value); setShowSourceDropdown(false); }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Room</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Paid By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoader && bookings.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <Loader text="Loading bookings…" size="30" />
                    </td>
                  </tr>
                ) : bookings.length > 0 ? (
                  bookings.map((b, i) => (
                    <tr key={b._id || i}>
                      <td className="booking-id-cell">
                        {b.booking_code || `#${b._id?.toString().slice(-6)}`}
                      </td>
                      <td>
                        <div className="customer-cell">
                          <img
                            className="customer-avatar"
                            src={avatarUrl(b.user_id, customerName(b))}
                            alt={customerName(b)}
                            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(customerName(b))}&background=E3F0FC&color=4F97D8&size=80`; }}
                          />
                          <span className="customer-name">
                            {customerName(b)}
                            {!b.user_id && <span className="badge badge-light-warning ms-2">Walking Customer</span>}
                          </span>
                        </div>
                      </td>
                      <td>{serviceName(b)}</td>
                      <td>{roomName(b)}</td>
                      <td>
                        <div className="date-cell">
                          <span className="date-day">{formatDay(b.booking_date)}</span>
                          {b.booking_time && <span className="date-time">{b.booking_time}</span>}
                        </div>
                      </td>
                      <td>
                        <span className={statusPillClass(b.status)}>
                          {(b.status || "N/A").toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${b.payment_by === "membership" ? "badge-light-info" : "badge-light-primary"}`}>
                          {b.payment_by ? b.payment_by.toUpperCase() : "—"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="row-action-btn"
                          title="View details"
                          onClick={() => handleBookingClick(b)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-calendar-x fs-1 text-muted mb-3"></i>
                        <span className="text-muted">No bookings found</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="bookings-footer">
            <div className="entries-info">
              Showing {fromEntry} to {toEntry} of {totalRecords} entries
            </div>
            {pagination.total_pages > 1 && (
              <Paginator
                page={pagination.current_page}
                rows={pagination.per_page}
                totalRecords={pagination.total_records}
                onPageChange={(p) => setApiData((prev) => ({ ...prev, page: p }))}
              />
            )}
          </div>
        </div>
      </div>

      {/* Booking Details Offcanvas */}
      <BookingDetailsOffcanvas
        bookingId={selectedBookingId}
        show={showBookingDetails}
        onClose={handleCloseOffcanvas}
        onUpdate={() => getBookings()}
      />
    </>
  );
};

export default BookingsList;
